import type {
  ConditionRangeItem,
  ConditionRangeValue,
} from '../conditions.types';
import type { BoundUnits } from './formatBound';
import { formatBound } from './formatBound';
import { toBoundLabel } from './toBoundLabel';

/**
 * The first problem with a selected range, or undefined when it is valid.
 *
 * An open bound is never an error — "at least $10" and "up to $50" are both
 * legitimate selections, so only the bounds that are present get checked. The
 * same goes for the item's own bounds: a section that does not cap its ceiling
 * has nothing for a maximum to exceed.
 */
export const validateRange = (
  item: Partial<ConditionRangeItem>,
  value: ConditionRangeValue,
  units?: BoundUnits
): string | undefined => {
  const { max, min } = value;

  // Equal bounds are allowed: it selects one exact value
  if (min !== undefined && max !== undefined && min > max) {
    return `${toBoundLabel('Minimum', item.label)} must be less than the maximum`;
  }

  if (min !== undefined && item.min !== undefined && min < item.min) {
    return `${toBoundLabel('Minimum', item.label)} must be at least ${formatBound(
      item.min,
      units
    )}`;
  }

  if (max !== undefined && item.max !== undefined && max > item.max) {
    return `${toBoundLabel('Maximum', item.label)} must be at most ${formatBound(
      item.max,
      units
    )}`;
  }

  return undefined;
};
